import { type FC, useState } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { TrackedDialog } from '@eneco/components/TrackedDialog/TrackedDialog';
import { Badge, Box, Button, Card, Divider, Grid, Heading, Image, Stack, Text, TextLink } from '@eneco/sparky';
import { CheckIcon, InfoIcon, PlusIcon } from '@eneco/sparky/icons';

import { Price } from '../../components';

interface InfoModal {
  title?: string;
  content?: string;
}

export interface OfferPriceRow {
  label?: string;
  price?: number;
  subtitle?: string;
}

export interface OfferItemCardProps {
  title?: string;
  titleBadgeText?: string;
  descriptionText?: string;
  priceText?: string;
  priceBadgeText?: string;
  subscriptionText?: string;
  subscriptionBadgeText?: string;
  imageSrc?: string;
  imageAlt?: string;
  addButtonText?: string;
  addToCart: () => void;
  addedButtonText?: string;
  discountDuration?: number;
  infoModal?: InfoModal;
  offerPrice?: number;
  subscriptionPrice?: number;
  isAdded: boolean;
  /** Hide the add/added button, e.g. for a primary offer that is always included. */
  showAddButton?: boolean;
  /** Extra one-off rows rendered above the `priceText`/`offerPrice` total row, e.g. a bundle's per-product breakdown. */
  priceRows?: OfferPriceRow[];
  /** Text rendered above the subscription rows, e.g. "Abonnementen per maand" for a bundle. */
  subscriptionSectionTitle?: string;
  /** Extra monthly rows rendered above the `subscriptionText`/`subscriptionPrice` total row. */
  subscriptionRows?: OfferPriceRow[];
}

/** Presentational offer product card, shared by flows that let a customer add/remove a product. */
export const OfferItemCard: FC<OfferItemCardProps> = ({
  title,
  titleBadgeText,
  descriptionText,
  priceText,
  priceBadgeText,
  subscriptionText,
  subscriptionBadgeText,
  imageSrc,
  imageAlt,
  addButtonText,
  addToCart,
  addedButtonText,
  discountDuration,
  infoModal,
  offerPrice,
  subscriptionPrice,
  isAdded,
  showAddButton = true,
  priceRows,
  subscriptionSectionTitle,
  subscriptionRows,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const monthValue = String(discountDuration ?? '');
  const titleBadgeContent = titleBadgeText?.replace('{month}', monthValue);
  const priceBadgeContent = priceBadgeText?.replace('{month}', monthValue);
  const subscriptionBadgeContent = subscriptionBadgeText?.replace('{month}', monthValue);

  return (
    <Card>
      <Box padding="6">
        <Stack gap="6">
          {imageSrc && (
            <Stack alignX="center">
              <Image src={imageSrc} alt={imageAlt ?? ''} width="100%" aspectRatio="16/9" objectFit="contain" />
            </Stack>
          )}

          <Stack gap="6">
            {title && (
              <Stack direction="row" gap="2" alignX="justify" alignY="start">
                <Heading as="h3" size="XS">
                  {title}
                </Heading>

                {infoModal?.title && (
                  <TrackedDialog
                    isOpen={isModalOpen}
                    setOpen={setIsModalOpen}
                    trigger={
                      <TextLink
                        emphasis="high"
                        ariaLabel={'Meer informatie'}
                        leftIcon={<InfoIcon color="currentColor" />}>
                        {''}
                      </TextLink>
                    }>
                    <Stack gap="6" direction="column">
                      {infoModal.title && (
                        <Heading size="XS" as="h2">
                          {infoModal.title}
                        </Heading>
                      )}

                      {infoModal.content && <RichText html={infoModal.content} />}
                    </Stack>
                  </TrackedDialog>
                )}
              </Stack>
            )}

            {titleBadgeText && (
              <Box>
                <Badge color="alpha">
                  <RichText html={titleBadgeContent} />
                </Badge>
              </Box>
            )}

            {descriptionText && <Text>{descriptionText}</Text>}
          </Stack>

          {priceRows?.map((row, index) => (
            <Grid columns="2" rowGap="4" key={row.label ?? index}>
              <Stack direction="column" gap="2">
                <Text size="BodyM" weight="bold">
                  {row.label}
                </Text>
                {row.subtitle && (
                  <Text size="BodyM" color="textLowEmphasis">
                    {row.subtitle}
                  </Text>
                )}
              </Stack>
              <Stack direction="column" gap="2" alignX="end">
                {typeof row.price === 'number' ? (
                  <Price suffix="" spaceAfterMinus={row.price < 0}>
                    {row.price}
                  </Price>
                ) : (
                  <Text> -</Text>
                )}
              </Stack>
            </Grid>
          ))}

          {priceRows && (
            <Grid.Item gridColumn="1/-1">
              <Divider emphasis="medium" />
            </Grid.Item>
          )}

          {priceText && (
            <Grid columns="2" rowGap="4">
              <Text size="BodyM" weight="bold">
                {priceText}
              </Text>
              <Stack direction="column" gap="2" alignX="end">
                {typeof offerPrice === 'number' ? <Price suffix="">{offerPrice}</Price> : <Text>-</Text>}
                {priceBadgeContent && <RichText html={priceBadgeContent} />}
              </Stack>
            </Grid>
          )}

          {subscriptionSectionTitle && (
            <>
              <Box paddingTop="6">
                <Text size="BodyM" weight="regular">
                  {subscriptionSectionTitle}
                </Text>
              </Box>
              <Grid.Item gridColumn="1/-1">
                <Divider />
              </Grid.Item>
            </>
          )}

          {subscriptionRows?.map((row, index) => (
            <Grid columns="2" rowGap="4" key={row.label ?? index}>
              <Stack direction="column" gap="2">
                <Text size="BodyM" weight="bold">
                  {row.label}
                </Text>
                {row.subtitle && (
                  <Text size="BodyS" color="textLowEmphasis">
                    {row.subtitle}
                  </Text>
                )}
              </Stack>
              <Stack direction="column" gap="2" alignX="end">
                {typeof row.price === 'number' ? (
                  <Price suffix="" spaceAfterMinus={row.price < 0}>
                    {row.price}
                  </Price>
                ) : (
                  <Text> -</Text>
                )}
              </Stack>
            </Grid>
          ))}

          {subscriptionText && (
            <Grid columns="2" rowGap="4">
              <Grid.Item gridColumn="1/-1">
                <Divider emphasis="medium" />
              </Grid.Item>
              <Text size="BodyM" weight="bold">
                {subscriptionText}
              </Text>
              <Stack direction="column" gap="2" alignX="end">
                {typeof subscriptionPrice === 'number' ? <Price suffix="">{subscriptionPrice}</Price> : <Text>-</Text>}
                {subscriptionBadgeContent && (
                  <Text size="BodyS" weight="regular" color="textLowEmphasis">
                    {subscriptionBadgeContent}
                  </Text>
                )}
              </Stack>
            </Grid>
          )}
          {showAddButton && (
            <Grid>
              {isAdded ? (
                <Button action="primary" onClick={addToCart}>
                  <CheckIcon />
                  {addedButtonText}
                </Button>
              ) : (
                <Button action="secondary" onClick={addToCart}>
                  <PlusIcon />
                  {addButtonText}
                </Button>
              )}
            </Grid>
          )}
        </Stack>
      </Box>
    </Card>
  );
};
